import { useScheduleStore } from '@/store/useScheduleStore'
import {
  buildCartRecoveryData,
  isCartRecoveryIdMismatch,
  isScheduleEmpty
} from './utils'
import { useCallback, useEffect } from 'react'
import {
  getAccessToken,
  getFromSessionStorage,
  setInSessionStorage
} from '@/utils/helpers/storageHelpers'
import { useCartRecoveryStore } from '@/store/useCartRecoveryStore'
import { storageKeys } from '@/shared/consts/storageKeys'
import { fillStepsParams } from '@/shared/consts/fillStepsParams'
import { useUserStore } from '@/store/useUserStore'
import { useIsActiveBreakPoint } from '@/hooks/useIsActiveBreakPoint'
import { sendEventWithoutStore } from '@/services/mixPanelService'
import { useSearchParams } from 'react-router-dom'

export const useCartRecovery = () => {
  const [searchParams] = useSearchParams()
  const hasParamnsInURL = Array.from(searchParams.keys()).some((param) =>
    fillStepsParams.includes(param)
  )

  const user = useUserStore((state) => state.user)
  const product = useScheduleStore((state) => state.product)
  const doctor = useScheduleStore((state) => state.doctor)
  const location = useScheduleStore((state) => state.location)
  const healthCarrier = useScheduleStore((state) => state.healthCarrier)
  const healthPlan = useScheduleStore((state) => state.healthPlan)
  const patient = useScheduleStore((state) => state.patient)
  const flowType = useScheduleStore((state) => state.flowType)
  const hasConfirmedSchedule = useScheduleStore(
    (state) => state.hasConfirmedSchedule
  )
  const {
    location: recoveryLocation,
    product: recoveryProduct,
    doctor: recoveryDoctor,
    payment: recoveryPayment,
    patient: recoveryPatient,
    alreadyRecovered,
    showModal,
    setLocation,
    setProduct,
    setDoctor,
    setPayment,
    setPatient,
    setFlowType,
    setShowModal,
    setHasCartRecovery,
    setAlreadyRecovered,
    setIsCheckingCartRecovery,
    updateSavedAt,
    reset
  } = useCartRecoveryStore((state) => state)

  const sessionStarted = getFromSessionStorage(storageKeys.CART_RECOVERY_DATA)
  const isMobile = useIsActiveBreakPoint('medium')
  const isLogged = getAccessToken()
  const hasDataInZustand = Boolean(
    recoveryLocation || recoveryProduct || recoveryPayment
  )

  const getParams = useCallback(() => {
    return [
      recoveryLocation && 'Local',
      recoveryProduct && 'Especialidade',
      recoveryPayment && 'Pagamento',
      recoveryPatient && 'Paciente'
    ].filter(Boolean)
  }, [recoveryLocation, recoveryProduct, recoveryPayment, recoveryPatient])

  const handleShowCartRecoveryModal = useCallback(() => {
    if (hasParamnsInURL) {
      reset()
      setIsCheckingCartRecovery(false)
      return
    }

    if (alreadyRecovered) {
      setIsCheckingCartRecovery(false)
      return
    }

    if (isLogged && user === null) return

    const recoveryUserId = recoveryPatient?.id
    const selectedUserId = user?.selected ? user?.id : undefined

    if (isLogged && !selectedUserId) {
      setIsCheckingCartRecovery(false)
      return
    }

    if (recoveryUserId && selectedUserId && recoveryUserId !== selectedUserId) {
      setIsCheckingCartRecovery(false)
      return
    }

    if (hasDataInZustand && !sessionStarted) {
      if (
        isLogged &&
        isCartRecoveryIdMismatch({ recoveryUserId, selectedUserId }) // só reseta localstorage se logado, não afeta deslogado
      ) {
        reset()
        setIsCheckingCartRecovery(false)
        return
      }

      setHasCartRecovery(true)
      setShowModal(true)
      setIsCheckingCartRecovery(false)
      return
    }

    if (!sessionStarted) {
      setInSessionStorage(storageKeys.CART_RECOVERY_DATA, 'true')
    }

    setIsCheckingCartRecovery(false)
  }, [
    hasParamnsInURL,
    alreadyRecovered,
    isLogged,
    user,
    recoveryPatient,
    hasDataInZustand,
    sessionStarted,
    setIsCheckingCartRecovery,
    reset,
    setHasCartRecovery,
    setShowModal
  ])

  const handleUpdateRecoveryData = useCallback(() => {
    if (alreadyRecovered) return
    //desktop deslogado não faz recuperação de carrinho
    if (!isMobile && !isLogged) return

    const recoveryData = buildCartRecoveryData({
      doctor,
      location,
      product,
      healthCarrier,
      healthPlan,
      patient:
        !isLogged && isMobile // mobile deslogado salva
          ? patient
          : user?.selected
            ? user
            : patient
    })

    if (!recoveryData) return
    if (recoveryData.location) setLocation(recoveryData.location)
    if (recoveryData.product) setProduct(recoveryData.product)
    if (recoveryData.doctor) setDoctor(recoveryData.doctor)
    if (recoveryData.payment) setPayment(recoveryData.payment)
    if (recoveryData.patient) setPatient(recoveryData.patient)

    updateSavedAt()
    setHasCartRecovery(true)
    setFlowType(flowType)
  }, [
    alreadyRecovered,
    isMobile,
    isLogged,
    doctor,
    location,
    product,
    healthCarrier,
    healthPlan,
    patient,
    user,
    setLocation,
    setProduct,
    setDoctor,
    setPayment,
    setPatient,
    updateSavedAt,
    setHasCartRecovery,
    setFlowType,
    flowType
  ])

  const handleClearCartRecovery = useCallback(() => {
    if (alreadyRecovered) return

    const schedule = useScheduleStore.getState()

    if (sessionStarted && isScheduleEmpty(schedule)) {
      reset()
    }
  }, [alreadyRecovered, sessionStarted, reset])

  const handleClearAlreadyRecovery = useCallback(() => {
    if (!sessionStarted && alreadyRecovered && !hasDataInZustand) {
      setAlreadyRecovered(false)
    }
  }, [sessionStarted, alreadyRecovered, setAlreadyRecovered, hasDataInZustand])

  const handleAcceptRecovery = useCallback(() => {
    sendEventWithoutStore({
      eventName: 'CART_RECOVERY',
      data: {
        ACTION: 'Aceitar',
        LOCAL_RDSL: recoveryLocation?.id,
        LOCAL_DESCRIPTION: recoveryLocation?.name,
        HEALTH_CARRIER_CODE: recoveryPayment?.idCarrier,
        HEALTH_CARRIER_NAME: recoveryPayment?.healthCarrier,
        HEALTH_PLAN_CODE: recoveryPayment?.idPlan,
        HEALTH_PLAN_NAME: recoveryPayment?.healthPlan,
        SPECIALTY_DESCRIPTION: recoveryProduct?.name,
        CONSULTATION_SPECIALTY_CODE: recoveryProduct?.id,
        PARAMS: getParams(),
        PAYMENT: recoveryPayment?.isPrivate ? 'Particular' : 'Convênio',
        DOCTOR_NAME: recoveryDoctor?.name,
        CONSULTATION_DOCTOR_CODE: recoveryDoctor?.id
      }
    })

    setShowModal(false)
  }, [
    getParams,
    recoveryLocation,
    recoveryPayment,
    recoveryProduct,
    recoveryDoctor,
    setShowModal
  ])

  const handleRejectRecovery = useCallback(() => {
    const baseEventData = {
      LOCAL_RDSL: recoveryLocation?.id,
      LOCAL_DESCRIPTION: recoveryLocation?.name,
      HEALTH_CARRIER_CODE: recoveryPayment?.idCarrier,
      HEALTH_CARRIER_NAME: recoveryPayment?.healthCarrier,
      HEALTH_PLAN_CODE: recoveryPayment?.idPlan,
      HEALTH_PLAN_NAME: recoveryPayment?.healthPlan,
      SPECIALTY_DESCRIPTION: recoveryProduct?.name,
      CONSULTATION_SPECIALTY_CODE: recoveryProduct?.id,
      PARAMS: getParams(),
      PAYMENT: recoveryPayment?.isPrivate ? 'Particular' : 'Convênio',
      DOCTOR_NAME: recoveryDoctor?.name,
      CONSULTATION_DOCTOR_CODE: recoveryDoctor?.id
    }

    sendEventWithoutStore({
      eventName: 'CART_RECOVERY',
      data: {
        ACTION: 'Recusar',
        ...baseEventData
      }
    })

    setAlreadyRecovered(true)
    reset()
    setInSessionStorage(storageKeys.CART_RECOVERY_DATA, 'true')
  }, [
    getParams,
    recoveryLocation,
    recoveryPayment,
    recoveryProduct,
    recoveryDoctor,
    reset,
    setAlreadyRecovered
  ])

  useEffect(() => {
    handleUpdateRecoveryData()
  }, [handleUpdateRecoveryData])

  useEffect(() => {
    handleClearCartRecovery()
  }, [handleClearCartRecovery])

  useEffect(() => {
    handleShowCartRecoveryModal()
  }, [handleShowCartRecoveryModal])

  useEffect(() => {
    handleClearAlreadyRecovery()
  }, [handleClearAlreadyRecovery])

  useEffect(() => {
    if (hasConfirmedSchedule) {
      reset()
      setAlreadyRecovered(true)
      setInSessionStorage(storageKeys.CART_RECOVERY_DATA, 'true')
    }
  }, [hasConfirmedSchedule, reset, setAlreadyRecovered])

  return {
    recoveryData: {
      location: recoveryLocation,
      product: recoveryProduct,
      doctor: recoveryDoctor,
      payment: recoveryPayment,
      patient: isMobile ? recoveryPatient : undefined // desktop nunca recebe patient
    },
    showModal,
    handleAcceptRecovery,
    handleRejectRecovery
  }
}
